import type { JSX, ReactNode } from 'react'
import type { Classe, Famille } from '@whatremains/shared'

/**
 * Les glyphes, dessinés.
 *
 * Même geste que `CarteDessinee` : des `<path>` écrits à la main, du trait et
 * rien d'autre. Aucun bitmap, aucune bibliothèque, aucun dégradé, aucun filtre.
 *
 * Deux jeux, deux échelles :
 *   - `GlypheFamille`, repère 24, affiché vers 22 px dans les listes d'inventaire.
 *   - `EmblemeClasse`, repère 48, affiché vers 90 px à la création de personnage.
 *
 * La couleur ne vit jamais ici : tout est en `currentColor`, et c'est le parent
 * qui décide (palier de rareté, ligne atténuée, sélection). C'est ce qui permet
 * à une même silhouette de porter les huit paliers sans être redessinée.
 *
 * Le seul critère qui compte pour les familles : **se distinguer à 22 px**. Une
 * épée et une dague ne diffèrent donc pas par la taille mais par la silhouette,
 * garde large contre garde absente, lame longue contre lame courte et poignée
 * longue. Tout ce qui ne survit pas à cette taille a été retiré.
 */

/** Le trait commun. Posé sur le `<svg>`, hérité par tous les enfants. */
const TRAIT = {
  fill: 'none',
  stroke: 'currentColor',
  strokeLinecap: 'round',
  strokeLinejoin: 'round',
} as const

/* ── Les dix-sept familles ────────────────────────────────────────────────────
 *
 * Repère `0 0 24 24`, tout le tracé tenu dans [2.7, 21.4] sur les deux axes, ce
 * qui laisse encore la demi-épaisseur du trait avant les 2 unités de marge. Elle
 * n'est pas décorative : c'est elle qui empêche le trait d'être rogné quand le
 * glyphe est posé dans une pastille ou un cadre de palier.
 */
const TRACES_FAMILLE: Record<Famille, ReactNode> = {
  // Lame longue, garde droite et large, pommeau plein. La garde est le marqueur.
  epee: (
    <>
      <path d="M 12 3 L 14.6 8 V 14.2 H 9.4 V 8 Z" />
      <path d="M 5.8 15 H 18.2" />
      <path d="M 12 15.2 V 18.8" />
      <circle cx="12" cy="20" r="1.3" />
    </>
  ),

  // Lame courte en losange, garde réduite à un ergot, longue poignée, pommeau annelé.
  dague: (
    <>
      <path d="M 12 3 L 15.2 8.2 L 12 12.4 L 8.8 8.2 Z" />
      <path d="M 9.8 12.9 H 14.2" />
      <path d="M 12 13.1 V 17.5" />
      <circle cx="12" cy="19.4" r="1.9" />
    </>
  ),

  // Manche DROIT, fer barbu greffé d'un seul côté, talon en contrepoids de l'autre.
  // Le manche oblique du premier jet, avec un fer qui s'éloignait en croissant,
  // Manche vertical qui dépasse en haut, fer en amande qui s'évase vers le haut
  // ET vers le bas, talon carré de l'autre côté.
  // Le premier jet accrochait un fer rectangulaire tout en haut du manche : ça
  // Manche vertical, fer attaché sur une COURTE portée du manche et évasé vers
  // le tranchant, qui est bien plus long que l'attache. Petit talon à l'opposé.
  // Deux impasses avant : un fer rectangulaire en haut du manche, puis un fer
  // bombé attaché sur toute sa hauteur. Les deux se lisaient comme un fanion sur
  // un mât. Ce qui fait la hache, c'est l'évasement : large au tranchant, étroit
  // au manche. Un drapeau fait l'inverse.
  hache: (
    <>
      <path d="M 11 3.8 V 21" />
      <path d="M 11 8 L 16.8 4.6 C 19.6 7 19.6 12.6 16.8 15 L 11 11.6 Z" />
      <path d="M 11 9 L 7.6 10.2 L 11 11.4" />
    </>
  ),

  // Tête bulbeuse hérissée en haut de hampe. La masse se lit à sa tête, jamais à sa hampe.
  masse: (
    <>
      <circle cx="12" cy="7.8" r="3.6" />
      <path d="M 12 4.2 V 3 M 9.45 5.25 L 8.3 4.1 M 14.55 5.25 L 15.7 4.1 M 8.4 7.8 H 6.6 M 15.6 7.8 H 17.4" />
      <path d="M 12 11.4 V 20.2" />
      <path d="M 9.8 11.9 H 14.2 M 10.2 20.4 H 13.8" />
    </>
  ),

  // Hampe en pleine diagonale, orbe en tête, deux ligatures. Rien d'autre : c'est un bâton.
  baton: (
    <>
      <path d="M 5.4 20.6 L 17.4 6.2" />
      <circle cx="18.6" cy="4.6" r="1.9" />
      <path d="M 8.4 14.5 L 10.8 16.6 M 10.2 12.4 L 12.6 14.4" />
    </>
  ),

  // Arc vertical : la corde tendue est droite, le bois est bombé. Deux poupées aux extrémités.
  arc: (
    <>
      <path d="M 8.2 3.4 C 16.5 8 16.5 16 8.2 20.6" />
      <path d="M 8.2 3.4 V 20.6" />
      <path d="M 6.8 4.4 L 9.6 2.8 M 6.8 19.6 L 9.6 21.2" />
    </>
  ),

  // Arc couché en travers d'un fût, corde TENDUE EN V vers la noix, carreau encoché
  // qui dépasse en haut. La corde droite du premier jet fermait l'arc en coupole :
  // le glyphe se lisait comme un parapluie. C'est le V qui fait l'arbalète.
  arbalete: (
    <>
      <path d="M 3.4 12.4 C 4.6 8.8 8 7.4 12 7.4 C 16 7.4 19.4 8.8 20.6 12.4" />
      <path d="M 3.4 12.4 L 12 15.6 L 20.6 12.4" />
      <path d="M 12 4 V 21" />
      <path d="M 10.4 6.6 L 12 4 L 13.6 6.6" />
      <path d="M 12 17.6 L 14.6 19.4" />
    </>
  ),

  // Écu à pointe basse, arête centrale. Pointe en bas : c'est ce qui le sépare du heaume.
  bouclier: (
    <>
      <path d="M 12 2.8 L 19.8 5.6 V 12.6 C 19.8 17 16.6 19.9 12 21.3 C 7.4 19.9 4.2 17 4.2 12.6 V 5.6 Z" />
      <path d="M 12 6 V 18.6" />
    </>
  ),

  // Calotte, fente de vue unique, gorgerin PLUS LARGE que la calotte à la base.
  // Deux impasses avant d'y arriver : base fermée en rectangle arrondi, ça donnait
  // un cadenas ; fentes obliques plus barreaux de ventail, ça donnait un crâne.
  // Calotte, nasal qui descend jusqu'au gorgerin, deux fentes de vue de part et
  // d'autre, et une base plus large que la calotte.
  // Trois impasses avant : base fermée en rectangle arrondi (cadenas), fentes
  // obliques et ventail (crâne), fente unique pleine largeur (arche de porte).
  // C'est le nasal qui fait le heaume : sans lui il n'y a pas de visage.
  heaume: (
    <>
      <path d="M 5.8 19.8 C 5 13.4 5.4 8 8.2 5.2 C 9.8 3.6 14.2 3.6 15.8 5.2 C 18.6 8 19 13.4 18.2 19.8" />
      <path d="M 6.8 12.2 H 10.4 M 13.6 12.2 H 17.2" />
      <path d="M 12 10.2 V 19.8" />
      <path d="M 4.6 20.4 H 19.4" />
    </>
  ),

  // Torse : deux épaules marquées en haut, taille resserrée en bas, arête sternale.
  plastron: (
    <>
      <path d="M 7 5.2 L 10.2 6.8 H 13.8 L 17 5.2 C 19 8.6 19.4 14.4 17.6 20.4 C 14 21.6 10 21.6 6.4 20.4 C 4.6 14.4 5 8.6 7 5.2 Z" />
      <path d="M 10.2 6.8 C 11 8.4 13 8.4 13.8 6.8" />
      <path d="M 12 9.2 V 19.8" />
    </>
  ),

  // Deux fourreaux courts à tête plate, deux sangles chacun. Courts et sanglés : avant-bras.
  /*
   * Deux sangles croisées dans le dos, réunies par un anneau de tirage, et les
   * deux boucles d'épaule.
   *
   * C'était un brassard, ce qui était faux deux fois : la famille s'appelait
   * `brassards` alors que ses dix pièces sont des harnais de portage sanglés au
   * DOS — bricoles, dossières, bâtis de hercheur — et un avant-bras n'a rien à
   * faire dans l'emplacement du dos. Enzo l'a signalé par l'autre bout : « c'est
   * une tête, aucun sens si c'est des brassards ».
   *
   * Le X est ce qui se lit à 22 px : c'est la silhouette d'un harnais vu de
   * dos, et rien d'autre dans le jeu ne l'utilise.
   */
  harnais: (
    <>
      <path d="M 6.6 4.2 L 17.4 15.4" />
      <path d="M 17.4 4.2 L 6.6 15.4" />
      <rect x="5.2" y="3" width="3" height="2.4" rx="0.6" />
      <rect x="15.8" y="3" width="3" height="2.4" rx="0.6" />
      <circle cx="12" cy="18.2" r="2.6" />
    </>
  ),

  // Une main : mitaine à quatre doigts, pouce détaché sur le côté, manchette.
  gantelets: (
    <>
      <path d="M 8 20.6 V 9.6 C 8 7.8 9.2 6.6 10.8 6.6 H 15.2 C 16.8 6.6 18 7.8 18 9.6 V 20.6 Z" />
      <path d="M 8 12.4 C 5.2 12.4 4.4 14.6 5.4 16.4 C 6 17.5 7 17.9 8 17.9" />
      <path d="M 11.3 6.8 V 10.2 M 14.4 6.8 V 10.2" />
      <path d="M 7.6 17.6 H 18.4" />
    </>
  ),

  // Bande horizontale, boucle à ardillon au milieu. Le seul glyphe couché du jeu.
  ceinture: (
    <>
      <path d="M 3.4 9.6 H 20.6 V 14.4 H 3.4 Z" />
      <path d="M 9.4 8 H 14.6 V 16 H 9.4 Z" />
      <path d="M 12 8.4 V 15.6" />
    </>
  ),

  // Deux grèves longues, coiffées d'une genouillère bombée. Le dôme les sépare des brassards.
  jambieres: (
    <>
      <path d="M 4.8 10 C 4.8 5.6 10.4 5.6 10.4 10 L 9.6 20.8 H 6 Z" />
      <path d="M 19.2 10 C 19.2 5.6 13.6 5.6 13.6 10 L 14.4 20.8 H 18 Z" />
      <path d="M 5 11.8 H 10.2 M 13.8 11.8 H 19" />
    </>
  ),

  // Une botte de profil : tige verticale, pied qui part en équerre, semelle et revers.
  bottes: (
    <>
      <path d="M 5.9 3.4 H 10.9 V 12.8 C 10.9 15 12.9 15.8 15.1 16.8 C 17.1 17.7 17.7 18.8 17.7 20.6 H 5.9 Z" />
      <path d="M 6.2 18.4 H 17.4" />
      <path d="M 5.9 6.6 H 10.9" />
    </>
  ),

  // Jonc fermé en bas, chaton en pointe au-dessus, deux griffes. Le plein est en bas.
  anneau: (
    <>
      <circle cx="12" cy="14.4" r="5.6" />
      <path d="M 12 3 L 14.8 6.2 L 12 9.4 L 9.2 6.2 Z" />
      <path d="M 10.2 8.2 L 9.4 9.8 M 13.8 8.2 L 14.6 9.8" />
    </>
  ),

  // Chaîne ouverte en haut, pendentif fermé en bas. L'exact inverse de l'anneau.
  amulette: (
    <>
      <path d="M 5.4 3.4 C 5.4 11 8.2 14.6 12 14.6 C 15.8 14.6 18.6 11 18.6 3.4" />
      <circle cx="12" cy="15.6" r="1.2" />
      <path d="M 12 16.8 L 15 19.05 L 12 21.3 L 9 19.05 Z" />
    </>
  ),
}

/* ── Les huit emblèmes ────────────────────────────────────────────────────────
 *
 * Repère `0 0 48 48`. Chacun part de la fantaisie centrale de sa classe, telle
 * qu'elle est écrite dans `CLASSE_INFO`, et d'elle seule : pas de silhouette
 * d'arme, pas de personnage, pas de visage, pas de scène. Ce sont des marques
 * héraldiques, et deux d'entre elles ne doivent jamais pouvoir être échangées.
 */
const TRACES_CLASSE: Record<Classe, ReactNode> = {
  /*
   * DIEU — un cercle fermé et rien dedans.
   *
   * Les huit autres marques disent une fantaisie de jeu. Celle-ci n'en a pas :
   * c'est une classe d'administration, hors du monde, et un cercle vide est la
   * seule chose honnête à mettre là. Il ne se confond avec aucune des huit,
   * précisément parce qu'il ne raconte rien.
   */
  dieu: (
    <>
      <circle cx="32" cy="32" r="21" />
      <circle cx="32" cy="32" r="13" />
    </>
  ),
  // Tenir la ligne : tout ce qui est au-dessus est rompu et de travers, en bas une
  // seule poutre continue, ancrée aux deux bouts. Les fragments sont volontairement
  // irréguliers : alignés proprement en trois rangs, ça se lisait comme une icône
  // Tenir la ligne : au-dessus, deux rangs rompus en morceaux décalés ; en bas,
  // une seule ligne continue, tenue par deux appuis. Ce qui a cédé et ce qui
  // n'a pas cédé, dans la même image.
  // Le premier jet dispersait des traits obliques irréguliers : l'idée y était,
  // Tenir la ligne : une seule verticale, et de part et d'autre des poussées qui
  // arrivent dessus et s'arrêtent. Rien ne la traverse et elle ne recule pas.
  // Deux impasses avant : des traits obliques dispersés au-dessus d'une barre,
  // qui se lisaient comme des rayures ; puis les mêmes rangés à l'horizontale
  // avec deux appuis sous la barre, qui donnaient un banc.
  guerrier: (
    <>
      <path d="M 24 6.5 V 41.5" />
      <path d="M 7 17 H 18 M 15 14 L 18 17 L 15 20" />
      <path d="M 7 31 H 18 M 15 28 L 18 31 L 15 34" />
      <path d="M 41 17 H 30 M 33 14 L 30 17 L 33 20" />
      <path d="M 41 31 H 30 M 33 28 L 30 31 L 33 34" />
    </>
  ),

  // Encaisser pour les autres : un rempart bombé, dont les bouts redescendent en
  // pavois, prend trois coups larges ; trois marques restent intactes derrière.
  // Les hampes fines du premier jet tombaient comme de la pluie sur un parapluie.
  gardien: (
    <>
      <path d="M 5 32 C 8 16 40 16 43 32" />
      <path d="M 11.8 10 V 22.9 M 7.8 18.4 L 11.8 22.9 L 15.8 18.4" />
      <path d="M 24 8 V 19.9 M 20 15.4 L 24 19.9 L 28 15.4" />
      <path d="M 36.2 10 V 22.9 M 32.2 18.4 L 36.2 22.9 L 40.2 18.4" />
      <circle cx="14" cy="39" r="2.4" />
      <circle cx="24" cy="39" r="2.4" />
      <circle cx="34" cy="39" r="2.4" />
    </>
  ),

  // Ne pas être vu : un losange entier, dont le milieu manque. La forme se
  // reconnaît, ce qu'il y a dedans se dérobe. Le premier jet posait quatre
  // chevrons autour d'un centre absent : l'idée était juste, mais à l'écran
  // ça se lisait comme des débris et pas comme un emblème.
  rogue: (
    <>
      <path d="M 11 20 L 24 6.5 L 37 20" />
      <path d="M 11 28 L 24 41.5 L 37 28" />
      <path d="M 19.5 24 H 28.5" />
    </>
  ),

  // Pister, tenir à distance : une ligne de visée graduée, la trace qui la longe, la cible cerclée au bout.
  eclaireur: (
    <>
      <path d="M 9.2 38.8 L 36.4 11.6" />
      <path d="M 14.2 30.2 L 17.8 33.8 M 20.2 24.2 L 23.8 27.8 M 26.2 18.2 L 29.8 21.8" />
      <path d="M 19.2 38.8 L 22.8 35.2 M 25.6 32.4 L 28.4 29.6 M 31.9 26.1 L 34.1 23.9" />
      <circle cx="8" cy="40" r="1.7" />
      <circle cx="39" cy="9" r="3.4" />
    </>
  ),

  // Plier les lois : cinq règles droites, et une masse qui les courbe d'autant plus
  // qu'elles en sont proches. La déviation est LOCALE, les tiers extérieurs restent
  // rigoureusement droits : étalée sur toute la largeur, elle faisait des vagues.
  mage: (
    <>
      <path d="M 6 9 H 42" />
      <path d="M 6 16 H 14 C 19 16 19.5 13.4 24 13.4 C 28.5 13.4 29 16 34 16 H 42" />
      <path d="M 6 24 H 13 C 18.5 24 19 20 24 20 C 29 20 29.5 24 35 24 H 42" />
      <path d="M 6 32 H 14 C 19 32 19.5 29.4 24 29.4 C 28.5 29.4 29 32 34 32 H 42" />
      <path d="M 6 39 H 42" />
      <circle cx="24" cy="24" r="3.6" />
    </>
  ),

  // Réparer, dissiper, prévoir : le cercle fendu tient par trois agrafes, sous un halo encore incomplet.
  oracle: (
    <>
      <circle cx="24" cy="27" r="12" />
      <path d="M 15.5 18.5 L 22 24.5 L 18.8 28.5 L 26 32 L 32.5 35.5" />
      <path d="M 20.5 19.6 L 17 23.4 M 23.5 27.9 L 21.3 32.6 M 30.5 31.5 L 28 36" />
      <path d="M 8.97 21.53 A 16 16 0 0 1 13.72 14.74 M 18.53 11.97 A 16 16 0 0 1 29.47 11.97 M 34.28 14.74 A 16 16 0 0 1 39.03 21.53" />
    </>
  ),

  // Ne jamais être seul : trois présences inégales, liées au centre et liées entre elles. Le lien est le sujet.
  invocateur: (
    <>
      <circle cx="24" cy="28" r="3.4" />
      <circle cx="24" cy="13" r="3" />
      <circle cx="37" cy="35.5" r="2.4" />
      <circle cx="11" cy="35.5" r="2.6" />
      <path d="M 24 24.6 V 16 M 26.9 29.7 L 34.9 34.3 M 21.1 29.7 L 13.3 34.2" />
      <path d="M 25.5 15.6 L 35.8 33.4 M 34.6 35.5 H 13.6 M 12.3 33.25 L 22.5 15.6" />
    </>
  ),

  // Amplifier les autres : trois marques modestes à la base, et le même signal repris trois fois plus large.
  heraut: (
    <>
      <circle cx="24" cy="39" r="2.2" />
      <circle cx="16" cy="39.4" r="1.4" />
      <circle cx="32" cy="39.4" r="1.4" />
      <path d="M 17 33 L 24 27 L 31 33" />
      <path d="M 12 27 L 24 16.5 L 36 27" />
      <path d="M 6 21 L 24 5.5 L 42 21" />
    </>
  ),
}

/**
 * Le glyphe d'une famille d'objet.
 *
 * Décoratif : le nom lisible est toujours écrit à côté en HTML, donc le `<svg>`
 * est masqué aux lecteurs d'écran. Un glyphe qui devrait porter le sens tout
 * seul serait de toute façon un mauvais glyphe.
 */
export function GlypheFamille({
  famille,
  taille,
}: {
  famille: Famille
  taille?: number
}): JSX.Element {
  const cote = taille ?? 24
  return (
    <svg
      viewBox="0 0 24 24"
      width={cote}
      height={cote}
      strokeWidth={1.4}
      aria-hidden="true"
      focusable="false"
      {...TRAIT}
    >
      {TRACES_FAMILLE[famille]}
    </svg>
  )
}

/**
 * L'emblème d'une classe.
 *
 * Plus grand, donc il peut porter davantage, mais il obéit aux mêmes règles :
 * du trait, `currentColor`, rien qui se peigne. Décoratif lui aussi, le nom de
 * la classe et sa fantaisie étant toujours écrits à côté.
 */
export function EmblemeClasse({
  classe,
  taille,
}: {
  classe: Classe
  taille?: number
}): JSX.Element {
  const cote = taille ?? 48
  return (
    <svg
      viewBox="0 0 48 48"
      width={cote}
      height={cote}
      strokeWidth={1.5}
      aria-hidden="true"
      focusable="false"
      {...TRAIT}
    >
      {TRACES_CLASSE[classe]}
    </svg>
  )
}
